// src/services/industry.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface Industry {
  id: string;
  name: string;
  category: string;
  icon?: string;
  hasSubIndustries: boolean;
  description?: string;
  summary?: string;
  shortDescription?: string;
  tags?: string[];
  priority?: number;
  lastUpdated?: string;
  growthRate?: number;
  marketSize?: string;
  investmentOpportunities?: number;
  keyMetrics?: Record<string, any>;
  growthData?: Array<{ year: string; value: number }>;
  tabs?: Record<string, any>;
  valueChain?: Record<string, any>;
  supportEcosystem?: Record<string, any>;
  opportunities?: string[];
  trends?: string[];
  subIndustries?: SubIndustry[];
  valueChainStages?: ValueChainStage[];
  supportEcosystemRecord?: SupportEcosystem;
  status?: 'draft' | 'pending_review' | 'published' | 'hidden';
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubIndustry {
  id: string;
  industryId: string;
  name: string;
  description?: string;
  summary?: string;
  growthRate?: number;
  marketSize?: string;
  investmentOpportunities?: number;
  keyMetrics?: Record<string, any>;
  growthData?: Array<{ year: string; value: number }>;
  tabs?: Record<string, any>;
  valueChain?: Record<string, any>;
  supportEcosystem?: Record<string, any>;
  valueChainStages?: ValueChainStage[];
  supportEcosystemRecord?: SupportEcosystem;
  subIndustries?: SubIndustry[];
  parentSubIndustryId?: string | null;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ValueChainStage {
  id: string;
  industryId?: string;
  subIndustryId?: string;
  name: string;
  title: string;
  content?: string;
  summary?: string;
  productsServicesNeeded?: string[];
  keyPlayers?: string[];
  trends?: string;
  challenges?: string;
  riskRating?: string;
  policyRecommendations?: string[];
  additionalData?: Record<string, any>;
  order: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportEcosystem {
  id: string;
  industryId?: string;
  subIndustryId?: string;
  summary?: string;
  productsServicesNeeded?: string[];
  keyPlayers?: string[];
  trends?: string;
  challenges?: string;
  riskRating?: string;
  policyRecommendations?: string[];
  talent?: Record<string, any>;
  funding?: Record<string, any>;
  supplyChain?: Record<string, any>;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================
// USER API ENDPOINTS (Authenticated Users)
// ============================================

/**
 * Get all industries
 */
export async function getAllIndustries(options?: {
  includeSubIndustries?: boolean;
  includeValueChain?: boolean;
  includeSupportEcosystem?: boolean;
}): Promise<Industry[]> {
  const params = new URLSearchParams();
  if (options?.includeSubIndustries) params.append('includeSubIndustries', 'true');
  if (options?.includeValueChain) params.append('includeValueChain', 'true');
  if (options?.includeSupportEcosystem) params.append('includeSupportEcosystem', 'true');

  const { data } = await api.get(`/api/industries?${params.toString()}`);
  return data.data.industries;
}

/**
 * Get a single industry by ID
 */
export async function getIndustryById(
  id: string,
  options?: {
    includeSubIndustries?: boolean;
    includeValueChain?: boolean;
    includeSupportEcosystem?: boolean;
  }
): Promise<Industry> {
  const params = new URLSearchParams();
  if (options?.includeSubIndustries) params.append('includeSubIndustries', 'true');
  if (options?.includeValueChain) params.append('includeValueChain', 'true');
  if (options?.includeSupportEcosystem) params.append('includeSupportEcosystem', 'true');

  const { data } = await api.get(`/api/industries/${id}?${params.toString()}`);
  return data.data.industry;
}

/**
 * Search industries
 */
export async function searchIndustries(
  query: string,
  options?: { category?: string; hasSubIndustries?: boolean }
): Promise<Industry[]> {
  const params = new URLSearchParams({ q: query });
  if (options?.category) params.append('category', options.category);
  if (options?.hasSubIndustries !== undefined) params.append('hasSubIndustries', String(options.hasSubIndustries));

  const { data } = await api.get(`/api/industries/search?${params.toString()}`);
  return data.data.industries;
}

/**
 * Get industries by category
 */
export async function getIndustriesByCategory(category: string): Promise<Industry[]> {
  const { data } = await api.get(`/api/industries/category/${category}`);
  return data.data.industries;
}

/**
 * Get sub-industries for an industry
 */
export async function getSubIndustries(
  industryId: string,
  options?: { includeValueChain?: boolean; includeSupportEcosystem?: boolean }
): Promise<SubIndustry[]> {
  const params = new URLSearchParams();
  if (options?.includeValueChain) params.append('includeValueChain', 'true');
  if (options?.includeSupportEcosystem) params.append('includeSupportEcosystem', 'true');

  const { data } = await api.get(`/api/industries/${industryId}/sub-industries?${params.toString()}`);
  return data.data.subIndustries;
}

/**
 * Get a single sub-industry by ID
 */
export async function getSubIndustryById(
  industryId: string,
  subId: string,
  options?: { includeValueChain?: boolean; includeSupportEcosystem?: boolean }
): Promise<SubIndustry> {
  const params = new URLSearchParams();
  if (options?.includeValueChain) params.append('includeValueChain', 'true');
  if (options?.includeSupportEcosystem) params.append('includeSupportEcosystem', 'true');

  const { data } = await api.get(`/api/industries/${industryId}/sub-industries/${subId}?${params.toString()}`);
  return data.data.subIndustry;
}

/**
 * Get value chain for an industry
 */
export async function getValueChain(industryId: string): Promise<ValueChainStage[]> {
  const { data } = await api.get(`/api/industries/${industryId}/value-chain`);
  return data.data.valueChain;
}

/**
 * Get value chain for a sub-industry
 */
export async function getSubIndustryValueChain(industryId: string, subId: string): Promise<ValueChainStage[]> {
  const { data } = await api.get(`/api/industries/${industryId}/sub-industries/${subId}/value-chain`);
  return data.data.valueChain;
}

/**
 * Get support ecosystem for an industry
 */
export async function getSupportEcosystem(industryId: string): Promise<SupportEcosystem | null> {
  const { data } = await api.get(`/api/industries/${industryId}/support-ecosystem`);
  return data.data.ecosystem;
}

/**
 * Get support ecosystem for a sub-industry
 */
export async function getSubIndustrySupportEcosystem(industryId: string, subId: string): Promise<SupportEcosystem | null> {
  const { data } = await api.get(`/api/industries/${industryId}/sub-industries/${subId}/support-ecosystem`);
  return data.data.ecosystem;
}

/**
 * Get support ecosystem details for an industry (array format for UI cards)
 */
export async function getSupportEcosystemDetails(industryId: string): Promise<any[]> {
  const { data } = await api.get(`/api/support-ecosystem/industry/${industryId}`);
  return data;
}

/**
 * Get support ecosystem details for a sub-industry (array format for UI cards)
 */
export async function getSubIndustrySupportEcosystemDetails(subIndustryId: string): Promise<any[]> {
  const { data } = await api.get(`/api/support-ecosystem/sub-industry/${subIndustryId}`);
  return data;
}

/**
 * Get a single support ecosystem detail by ID
 */
export async function getSupportEcosystemDetailById(id: string): Promise<any> {
  const { data } = await api.get(`/api/support-ecosystem/${id}`);
  return data;
}

// ============================================
// ADMIN API ENDPOINTS
// ============================================

/**
 * Get all industries (Admin - includes inactive)
 */
export async function getAllIndustriesAdmin(options?: {
  includeSubIndustries?: boolean;
  includeValueChain?: boolean;
  includeSupportEcosystem?: boolean;
}): Promise<Industry[]> {
  const params = new URLSearchParams();
  if (options?.includeSubIndustries) params.append('includeSubIndustries', 'true');
  if (options?.includeValueChain) params.append('includeValueChain', 'true');
  if (options?.includeSupportEcosystem) params.append('includeSupportEcosystem', 'true');

  const { data } = await api.get(`/api/admin/industries?${params.toString()}`);
  return data.data.industries;
}

/**
 * Create a new industry (Admin only)
 */
export async function createIndustry(industryData: Partial<Industry>): Promise<Industry> {
  const { data } = await api.post('/api/admin/industries', industryData);
  return data.data.industry;
}

/**
 * Update an industry (Admin only)
 */
export async function updateIndustry(id: string, updates: Partial<Industry>): Promise<Industry> {
  const { data } = await api.patch(`/api/admin/industries/${id}`, updates);
  return data.data.industry;
}

/**
 * Update specific tab for an industry (Admin only)
 */
export async function updateIndustryTab(id: string, tabName: string, tabData: any): Promise<Industry> {
  const { data } = await api.patch(`/api/admin/industries/${id}/tabs/${tabName}`, tabData);
  return data.data.industry;
}

/**
 * Update value chain data for an industry (Admin only)
 */
export async function updateIndustryValueChain(id: string, valueChainData: Record<string, any>): Promise<Industry> {
  const { data } = await api.patch(`/api/admin/industries/${id}/value-chain`, valueChainData);
  return data.data.industry;
}

/**
 * Update support ecosystem data for an industry (Admin only)
 */
export async function updateIndustrySupportEcosystem(id: string, ecosystemData: Record<string, any>): Promise<Industry> {
  const { data } = await api.patch(`/api/admin/industries/${id}/support-ecosystem`, ecosystemData);
  return data.data.industry;
}

/**
 * Delete an industry (Admin only - soft delete)
 */
export async function deleteIndustry(id: string): Promise<void> {
  await api.delete(`/api/admin/industries/${id}`);
}

/**
 * Create a new sub-industry (Admin only)
 */
export async function createSubIndustry(industryId: string, subIndustryData: Partial<SubIndustry>): Promise<SubIndustry> {
  const { data } = await api.post(`/api/admin/industries/${industryId}/sub-industries`, subIndustryData);
  return data.data.subIndustry;
}

/**
 * Update a sub-industry (Admin only)
 */
export async function updateSubIndustry(industryId: string, subId: string, updates: Partial<SubIndustry>): Promise<SubIndustry> {
  const { data } = await api.patch(`/api/admin/industries/${industryId}/sub-industries/${subId}`, updates);
  return data.data.subIndustry;
}

/**
 * Update specific tab for a sub-industry (Admin only)
 */
export async function updateSubIndustryTab(industryId: string, subId: string, tabName: string, tabData: any): Promise<SubIndustry> {
  const { data } = await api.patch(`/api/admin/industries/${industryId}/sub-industries/${subId}/tabs/${tabName}`, tabData);
  return data.data.subIndustry;
}

/**
 * Update value chain data for a sub-industry (Admin only)
 */
export async function updateSubIndustryValueChain(industryId: string, subId: string, valueChainData: Record<string, any>): Promise<SubIndustry> {
  const { data } = await api.patch(`/api/admin/industries/${industryId}/sub-industries/${subId}/value-chain`, valueChainData);
  return data.data.subIndustry;
}

/**
 * Update support ecosystem data for a sub-industry (Admin only)
 */
export async function updateSubIndustrySupportEcosystem(industryId: string, subId: string, ecosystemData: Record<string, any>): Promise<SubIndustry> {
  const { data } = await api.patch(`/api/admin/industries/${industryId}/sub-industries/${subId}/support-ecosystem`, ecosystemData);
  return data.data.subIndustry;
}

/**
 * Delete a sub-industry (Admin only - soft delete)
 */
export async function deleteSubIndustry(industryId: string, subId: string): Promise<void> {
  await api.delete(`/api/admin/industries/${industryId}/sub-industries/${subId}`);
}

/**
 * Create a value chain stage (Admin only)
 */
export async function createValueChainStage(
  industryId: string,
  stageData: Partial<ValueChainStage>,
  subIndustryId?: string
): Promise<ValueChainStage> {
  const endpoint = subIndustryId
    ? `/api/admin/industries/${industryId}/sub-industries/${subIndustryId}/value-chain-stages`
    : `/api/admin/industries/${industryId}/value-chain-stages`;

  const { data } = await api.post(endpoint, stageData);
  return data.data.stage;
}

/**
 * Update a value chain stage (Admin only)
 */
export async function updateValueChainStage(industryId: string, stageId: string, updates: Partial<ValueChainStage>): Promise<ValueChainStage> {
  const { data } = await api.patch(`/api/admin/industries/${industryId}/value-chain-stages/${stageId}`, updates);
  return data.data.stage;
}

/**
 * Delete a value chain stage (Admin only)
 */
export async function deleteValueChainStage(industryId: string, stageId: string): Promise<void> {
  await api.delete(`/api/admin/industries/${industryId}/value-chain-stages/${stageId}`);
}

/**
 * Create a support ecosystem (Admin only)
 */
export async function createSupportEcosystemRecord(
  industryId: string,
  ecosystemData: Partial<SupportEcosystem>,
  subIndustryId?: string
): Promise<SupportEcosystem> {
  const endpoint = subIndustryId
    ? `/api/admin/industries/${industryId}/sub-industries/${subIndustryId}/support-ecosystem-record`
    : `/api/admin/industries/${industryId}/support-ecosystem-record`;

  const { data } = await api.post(endpoint, ecosystemData);
  return data.data.ecosystem;
}

/**
 * Update a support ecosystem (Admin only)
 */
export async function updateSupportEcosystemRecord(industryId: string, ecosystemId: string, updates: Partial<SupportEcosystem>): Promise<SupportEcosystem> {
  const { data } = await api.patch(`/api/admin/industries/${industryId}/support-ecosystem-record/${ecosystemId}`, updates);
  return data.data.ecosystem;
}

/**
 * Delete a support ecosystem (Admin only)
 */
export async function deleteSupportEcosystemRecord(industryId: string, ecosystemId: string): Promise<void> {
  await api.delete(`/api/admin/industries/${industryId}/support-ecosystem-record/${ecosystemId}`);
}

// ============================================
// INDUSTRY STATUS MANAGEMENT (Admin only)
// ============================================

/**
 * Submit industry for review (Admin only)
 */
export async function submitIndustryForReview(industryId: string): Promise<Industry> {
  const { data } = await api.post(`/api/admin/industries/${industryId}/submit`);
  return data.data.industry;
}

/**
 * Approve and publish industry (Super Admin only)
 */
export async function approveIndustry(industryId: string): Promise<Industry> {
  const { data } = await api.post(`/api/admin/industries/${industryId}/approve`);
  return data.data.industry;
}

/**
 * Hide industry (Super Admin only)
 */
export async function hideIndustry(industryId: string): Promise<Industry> {
  const { data } = await api.post(`/api/admin/industries/${industryId}/hide`);
  return data.data.industry;
}

/**
 * Permanently delete industry (Super Admin only)
 */
export async function permanentlyDeleteIndustry(industryId: string): Promise<void> {
  await api.delete(`/api/admin/industries/${industryId}/permanent`);
}
